/**
 * An axios instance for the recording pipeline (create/upload/complete/poll/delete), which calls
 * the API outside apiService's instance and so missed its refresh-on-401.
 *
 * Before each request it makes sure the access token has at least two minutes left, refreshing
 * if not, and reads the header at that moment: a clinician who finishes a recording after the
 * device slept or the tab was hidden would otherwise upload with a token the timer did not get to
 * renew. On a 401 it refreshes and retries the request once. Uploads keep their body, signal,
 * timeout and progress callback across the retry.
 */
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { apiService } from './api';

const MIN_REMAINING_MS = 2 * 60 * 1000;

type RetriableConfig = InternalAxiosRequestConfig & { _authRetried?: boolean };

export const authorizedAxios = axios.create();

authorizedAxios.interceptors.request.use(async (config) => {
  // A retry already carries the token the 401 handler just obtained.
  if ((config as RetriableConfig)._authRetried) return config;
  const token = await apiService.ensureFreshAccessToken(MIN_REMAINING_MS);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

authorizedAxios.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as RetriableConfig | undefined;
    if (error.response?.status === 401 && original && !original._authRetried) {
      original._authRetried = true;
      const token = await apiService.refreshAfterUnauthorized();
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return authorizedAxios(original);
      }
    }
    return Promise.reject(error);
  }
);
